import {
  BasemapTweakHideBuiltEnv,
  BasemapTweakHideGreen,
  BasemapTweakHideLabels
} from '@/config/map'
import { removeInvalidItems } from '@/services/adapters/transformations/removeInvalidItems'
import {
  ISanityTip,
  transformSanityTips
} from '@/services/adapters/transformations/transformSanityTips'
import { colorHex } from '@/utils/colors'
import { sortLayerValuesByMinMax } from '@/utils/layers/dataLayers'
import generateTemplate from '@/utils/layers/styleFactory/template'
import {
  DataSourceSchema,
  IBaseDataLayer,
  IBaseDataLayerReference,
  IBlockContent,
  IDataSource,
  ILayerConfig,
  ILayerPack,
  ILayerValue,
  ILayerValueReference,
  IMapLayers,
  IStyleConfig,
  LayerValueReferenceSchema,
  LayerValueSchema,
  TBasemapTweak,
  TDataLayerStyle,
  TExcludeLayer,
  TLayerType,
  TRadiusType
} from '@lifk/types'
import { capitalizeFirstLetter, groupMulti } from '@lifk/utils'
import { z } from 'zod'

/*
  Group LayerValues by tileset, style, zoom, to render each group as one mapbox layer.
  This prevents unnecessarily rendering the same tileset multiple times,
    when styling can be applied conditionally instead.
  Each mapbox layer can only have 1 tilesetUrl, tilesetLayer, style, and minzoom,
    so we group LayerValues by those fields here.
  */
const groupByTilesetAndZoom = (
  layerValues: ILayerValue[]
): Record<string, ILayerValue[]> => {
  return groupMulti(layerValues, (layerValue) => [
    `${layerValue.tilesetUrl}.${layerValue.tilesetLayer}.${layerValue.style}.${
      layerValue.layerConfig.minzoom ?? 0
    }.${layerValue.labelConfig.minzoom ?? 0}`
  ])
}

const createMapLayers = (
  dataLayer: Omit<IBaseDataLayer, 'mapLayers'>,
  groupedLayers: Record<string, ILayerValue[]>
): IMapLayers[] => {
  return Object.keys(groupedLayers).map((tileset) => {
    const layerValues = sortLayerValuesByMinMax(groupedLayers[tileset])
    const mapboxLayers = generateTemplate(dataLayer, layerValues)
    return {
      mapboxLayers,
      layerValues
    }
  })
}

const getMapLayers = (
  dataLayer: Omit<IBaseDataLayer, 'mapLayers'>,
  layerValues: ILayerValue[]
): IMapLayers[] => {
  const groupedByTilesetAndZoom = groupByTilesetAndZoom(layerValues)
  return createMapLayers(dataLayer, groupedByTilesetAndZoom)
}

/*
  Sanity response interfaces imitate UI interfaces as closely as possible.
  Some properties need to be more flexible, and overridden here to reflect the differences.

  (Eg- mapbox Expressions come from Sanity as JSON.stringified strings,
    and must be parsed into Expressions to fit the UI interfaces)
*/
interface ISanityStyleConfig
  extends Omit<IStyleConfig, 'modulator' | 'radius' | 'exclude'> {
  modulator: string
  radius: string
  exclude: string[]
}
interface ISanityLayerConfig extends Omit<ILayerConfig, 'filter'> {
  filter: string
}

interface ISanityLayerValue
  extends Omit<
    ILayerValueReference,
    'style' | 'styleConfig' | 'layerConfig' | 'labelConfig' | 'tilesetLayer'
  > {
  style: string
  tilesetLayer: string | undefined
  styleConfig: ISanityStyleConfig
  layerConfig: ISanityLayerConfig
  labelConfig: ISanityLayerConfig
}

export interface ISanityDataLayer
  extends Omit<
    IBaseDataLayerReference,
    | 'name'
    | 'lastUpdatedYear'
    | 'shortDescription'
    | 'longDescription'
    | 'methodology'
    | 'layerType'
    | 'basemapTweaks'
    | 'tips'
  > {
  name: string
  lastUpdatedYear: string
  shortDescription: string
  layerType: string
  basemapTweaks?: {
    hideLabels: boolean
    hideBuiltEnvironment: boolean
    hideGreen: boolean
  }
  layerValues: ISanityLayerValue[]

  // optionals fetched for Reference articles only
  longDescription?: string
  methodology?: IBlockContent
  es: {
    name: string
    shortDescription: string
    longDescription?: string
    methodology?: IBlockContent
  }[]
  tips?: ISanityTip[]
}

const sanitizeStrArrs = (arr: string[] | undefined) =>
  (arr ?? []).map((attr) => attr.trim()).filter((attr) => attr?.length)

const transformLayerValues = (
  sanityLayerValues: ISanityLayerValue[],
  isReference: boolean,
  disableValidations: boolean
): ILayerValue[] => {
  const layerValues = sanityLayerValues.map((slv) => {
    try {
      // style is required to build the template
      if (!slv?.key || !slv.style?.length) return

      const style = capitalizeFirstLetter(slv.style) as TDataLayerStyle
      const headings = sanitizeStrArrs(slv.headings)

      let layerValue: ILayerValue | ILayerValueReference = {
        ...slv,
        style,
        tilesetLayer: style === 'Raster' ? 'rasterarray' : slv.tilesetLayer,
        headings:
          style === 'Raster' || style === 'Animated (particles)'
            ? [...headings, 'band-1']
            : headings,
        muted: sanitizeStrArrs(slv.muted),
        styleConfig: {
          ...slv.styleConfig,
          color: slv.styleConfig.color ?? colorHex('poppy'),
          targetAttribute:
            style === 'Raster' || style === 'Animated (particles)'
              ? 'band-1'
              : slv.styleConfig.targetAttribute,
          radius:
            slv.styleConfig.radius && slv.styleConfig.radius !== 'default'
              ? (`$${slv.styleConfig.radius}` as TRadiusType)
              : undefined,
          modulator: slv.styleConfig.modulator
            ? JSON.parse(slv.styleConfig.modulator)
            : undefined,
          exclude: sanitizeStrArrs(slv.styleConfig.exclude) as TExcludeLayer[]
        },
        layerConfig: {
          ...slv.layerConfig,
          filter: slv.layerConfig?.filter
            ? JSON.parse(slv.layerConfig.filter)
            : slv.styleConfig.targetAttribute // if target attribute, filter for only that
              ? ['has', slv.styleConfig.targetAttribute]
              : undefined
        },
        labelConfig: {
          ...slv.labelConfig,
          filter: slv.labelConfig?.filter
            ? JSON.parse(slv.labelConfig.filter)
            : undefined
        }
      }

      if (isReference) {
        const validation = z
          .array(DataSourceSchema)
          .safeParse(slv.dataSources ?? [])
        let dataSources: IDataSource[] = validation.data ?? []
        if (!validation.success) {
          dataSources = removeInvalidItems(
            slv.dataSources ?? [],
            validation.error,
            disableValidations
          )
        }
        layerValue = {
          ...layerValue,
          dataSources
        } as ILayerValueReference
      }

      return layerValue
    } catch (e) {
      console.warn(`Error parsing Sanity layer value with id: ${slv?.key}`, e)
      return undefined
    }
  })

  // Validate and remove invalid items
  const validation = z
    .array(isReference ? LayerValueReferenceSchema : LayerValueSchema)
    .safeParse(layerValues)
  let validLayerValues: (ILayerValue | ILayerValueReference)[] =
    validation.data ?? []
  if (!validation.success) {
    validLayerValues = removeInvalidItems(
      layerValues,
      validation.error,
      disableValidations
    )
  }
  return validLayerValues
}

const getBasemapTweaks = (sanityDataLayer: ISanityDataLayer) => {
  const basemapTweaks: TBasemapTweak[] = []
  if (sanityDataLayer.basemapTweaks?.hideLabels)
    basemapTweaks.push(BasemapTweakHideLabels)
  if (sanityDataLayer.basemapTweaks?.hideBuiltEnvironment)
    basemapTweaks.push(BasemapTweakHideBuiltEnv)
  if (sanityDataLayer.basemapTweaks?.hideGreen)
    basemapTweaks.push(BasemapTweakHideGreen)
  return basemapTweaks
}

export const transformDataLayer = (
  sanityDataLayer: ISanityDataLayer,
  isReferenceArticle: boolean,
  disableValidations: boolean
): IBaseDataLayer | IBaseDataLayerReference | undefined => {
  try {
    const spanishTranslations =
      sanityDataLayer.es && sanityDataLayer.es[0]
        ? sanityDataLayer.es[0]
        : undefined

    // If image doesn't exist, replace with default static image
    //  (only used for ?perspective=drafts)
    const defaultLayerLibraryCard = {
      url: `/images/datasets/heat.jpg`,
      altText: {
        english: sanityDataLayer.name,
        spanish: spanishTranslations?.name
      }
    }

    // Layer type is required - skip if missing
    const layerType = sanityDataLayer.layerType
      ? (sanityDataLayer.layerType as TLayerType)
      : undefined
    if (!layerType) return undefined

    const dataLayer = {
      ...sanityDataLayer,
      lastUpdatedYear: parseInt(sanityDataLayer.lastUpdatedYear.slice(0, 4)),
      name: {
        english: sanityDataLayer.name,
        spanish: spanishTranslations?.name ?? ''
      },
      shortDescription: {
        english: sanityDataLayer.shortDescription,
        spanish: spanishTranslations?.shortDescription ?? ''
      },
      isGradient: sanityDataLayer.isGradient ?? false,
      showGreaterThan: sanityDataLayer.showGreaterThan ?? false,
      // feature layers are always false (ie- empty means a lack of features, and does not indicate "no data")
      emptyIsNoData: layerType !== 'feature' && !!sanityDataLayer.emptyIsNoData,
      layerType,
      basemapTweaks: getBasemapTweaks(sanityDataLayer),
      mapScreenshot: sanityDataLayer.mapScreenshot ?? defaultLayerLibraryCard
    }

    const layerValues = transformLayerValues(
      sanityDataLayer.layerValues ?? [],
      isReferenceArticle,
      disableValidations
    )

    let typedDataLayer: IBaseDataLayer | IBaseDataLayerReference = {
      ...dataLayer,
      mapLayers: getMapLayers(dataLayer, layerValues)
    }

    if (isReferenceArticle) {
      typedDataLayer = {
        ...typedDataLayer,
        longDescription: {
          english: sanityDataLayer.longDescription ?? '',
          spanish: spanishTranslations?.longDescription ?? ''
        },
        methodology: {
          english: sanityDataLayer.methodology ?? { blocks: [] },
          spanish: spanishTranslations?.methodology ?? { blocks: [] }
        },
        tips: transformSanityTips(
          sanityDataLayer.tips ?? [],
          disableValidations
        )
      }
    }

    return typedDataLayer
  } catch (e) {
    console.warn(
      `Error parsing Sanity data layer with id: ${sanityDataLayer?.key}`,
      e
    )
    return undefined
  }
}

export const transformSanityLayerPacks = (
  sanityLayerPacks: (Partial<ILayerPack> | undefined)[]
): ILayerPack[] =>
  sanityLayerPacks
    .map((sanityLayerPack) => {
      try {
        if (!sanityLayerPack?._id || !sanityLayerPack?.effect) return undefined
        return {
          ...sanityLayerPack,
          dataLayers: sanityLayerPack.dataLayers ?? []
        } as ILayerPack
      } catch (e) {
        console.warn(
          `Error parsing Sanity layer pack with id: ${sanityLayerPack?._id}`,
          e
        )
        return undefined
      }
    })
    .filter((lp) => !!lp)
